import styled from 'styled-components';
import { useContext } from 'react';

import Typography from '../../../kingpin/atoms/Typography';
import Row from '../../Common/Row';
import Icon from '../../../kingpin/atoms/Icon';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import useValueFormatters from '../../../hooks/useValueFormatters';
import getIdentifier from '../../../getIdentifier';
import { BonusOverviewContext } from '../providers/BonusOverviewProvider';

const Outer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 240px;
  background-color: var(--color-tooltip-bg);
  border-radius: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--color-tooltip-divider);
`;

const TierRow = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 2px 8px;
  border-radius: 6px;
  background-color: ${({ isActive }) =>
    isActive ? 'var(--color-tooltip-row-active-bg)' : 'transparent'};
`;

const SummarySection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--color-tooltip-divider);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryRow = ({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) => (
  <Row spaceBetween centerAlign style={{ gap: 24, padding: '0px 8px' }}>
    <Typography.Body
      type={bold ? 'Body 13 Bold' : 'Body 13'}
      color={'var(--color-tooltip-text)'}
    >
      {label}
    </Typography.Body>
    <Typography.Body
      type={bold ? 'Body 13 Bold' : 'Body 13'}
      color={'var(--color-tooltip-text)'}
    >
      {value}
    </Typography.Body>
  </Row>
);

/**
 * Tooltip content for a derived/tiered reward (e.g. tenure `scaledDerivedBanded`): the band ladder
 * — each tier's threshold and, for derived rewards, its multiplier factor — with the driver's
 * active tier marked. When a `base` is supplied (the $ pay the multiplier applies to) it also shows
 * the driver's math: base pay × multiplier = final pay. Falls back to threshold → payout for plain
 * banded rewards.
 */
const RewardTierTooltip = ({
  field,
  fieldValue,
  bands,
  base,
}: {
  field: string;
  fieldValue: number;
  bands: BonusTypes.Rewards.RewardBand[];
  base?: number;
}) => {
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField, formatValue } = useValueFormatters();

  if (!bonusOverview) {
    return null;
  }

  const { dataset, rewardType } = bonusOverview.bonusProgram;
  const isDerived = base !== undefined;
  const activeFactor = bands.find((b) => b.active)?.value;
  // Read the driver's final pay straight from the backend (not derived from base × multiplier).
  // Recognition payments are added to totalPay after the multiplier, so take
  // them back out here for the sum to read base × multiplier. A disqualified
  // driver's payments were never added.
  const { rewardOutput } = bonusOverview;
  const appliedAdditionalPayments =
    rewardOutput.eligibleForBonus === 'qualified'
      ? rewardOutput.totalAdditionalPayments ?? 0
      : 0;
  const finalPay =
    'totalPay' in rewardOutput
      ? rewardOutput.totalPay - appliedAdditionalPayments
      : undefined;

  const formatMoney = (value: number): string =>
    rewardType === 'points'
      ? `${formatValue({ value, formatting: { precision: 0 } })} PTS`
      : formatValue({
          value,
          formatting: { prefix: 'currency', precision: 2 },
        }).toString();

  const formatThreshold = (band: BonusTypes.Rewards.RewardBand): string => {
    const value = band.min ?? band.max ?? band.equal;
    if (value === undefined) {
      return '';
    }
    const operator = band.min != null ? '≥' : band.max != null ? '≤' : '=';
    return `${operator} ${formatField({ field, dataset, value })}`;
  };

  return (
    <Outer>
      <TitleRow>
        <Typography.Body
          type={'Body 14 Bold'}
          color={'var(--color-tooltip-text)'}
        >
          {getFieldLabel({ field, dataType: dataset })}
        </Typography.Body>
        <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
          {formatField({ field, dataset, value: fieldValue })}
        </Typography.Body>
      </TitleRow>
      {bands.map((band) => {
        const color = band.active
          ? 'var(--color-tooltip-text)'
          : 'var(--color-tooltip-text-muted)';
        const bodyType = band.active ? 'Body 13 Bold' : 'Body 13';
        // Non-derived banded rewards (e.g. ACT's `fixed` "Drive Your Pay") carry the per-band
        // payout in `band.value`; `band.reward` is null at runtime for these, so fall back to
        // `value` rather than leaving the right-hand cell blank.
        const bandAmount = band.reward ?? band.value;
        const right = isDerived
          ? `${band.value}×`
          : bandAmount == null
            ? ''
            : formatMoney(bandAmount);
        return (
          <TierRow
            key={getIdentifier(undefined, true)}
            isActive={!!band.active}
          >
            <Row centerAlign style={{ gap: 4 }}>
              {band.active && (
                <Icon
                  icon={'checkmark'}
                  color={'var(--color-status-success)'}
                  width={14}
                  height={14}
                />
              )}
              <Typography.Body type={bodyType} color={color}>
                {formatThreshold(band)}
              </Typography.Body>
            </Row>
            <Typography.Body type={bodyType} color={color}>
              {right}
            </Typography.Body>
          </TierRow>
        );
      })}
      {isDerived && (
        <SummarySection>
          <SummaryRow label={'Base pay'} value={formatMoney(base)} />
          <SummaryRow
            label={'Multiplier'}
            value={activeFactor === undefined ? '—' : `${activeFactor}×`}
          />
          {finalPay !== undefined && (
            <SummaryRow
              label={'Final pay'}
              value={formatMoney(finalPay)}
              bold
            />
          )}
        </SummarySection>
      )}
    </Outer>
  );
};

export default RewardTierTooltip;
